import type { Dispatch, SetStateAction } from 'react'
import type { TooltipMetadataItem } from './metadataManagement'
import type { ReleaseNoteItem } from './releaseNotes'

const settingsTabIds = [
    'content-management',
    'metadata-management',
    'user-management',
    'release-notes',
    'site-feedback'
]

export const getActiveTabFromParams = (searchParams: any): number => {
    const tab = searchParams.get('tab')
    if (!tab) return 0
    const index = settingsTabIds.indexOf(tab)
    return index !== -1 ? index : 0
}

// still needed for local-state release notes
const getNextId = <T extends { ID: number }>(items: T[]) =>
    items.length ? Math.max(...items.map(i => i.ID)) + 1 : 1

export const createUpdateTooltipItem = (
    setTooltipItems: Dispatch<SetStateAction<TooltipMetadataItem[]>>
) => async (
    payload: Pick<TooltipMetadataItem, 'TooltipDescription'>,
    original: TooltipMetadataItem
) => {
    setTooltipItems(previous => previous.map(item => item.ID === original.ID
        ? { ...item, TooltipDescription: payload.TooltipDescription, ItemModified: new Date().toISOString() }
        : item))
    return { success: true }
}

export const addTooltipItem = async () => ({ success: false })
export const deleteTooltipItem = async () => ({ success: false })

type ReleaseNoteFormPayload = Omit<ReleaseNoteItem, 'ID' | 'IsArchived'> & {
    IsArchived: ReleaseNoteItem['IsArchived'] | 'Yes' | 'No'
}

const normalizeReleaseNotePayload = (payload: ReleaseNoteFormPayload): Omit<ReleaseNoteItem, 'ID'> => ({
    ...payload,
    Modified: payload.Modified ?? new Date().toISOString(),
    IsArchived: payload.IsArchived === true || payload.IsArchived === 'Yes'
})

export const createAddReleaseNote = (
    releaseNotes: ReleaseNoteItem[],
    setReleaseNotes: Dispatch<SetStateAction<ReleaseNoteItem[]>>
) => async (payload: ReleaseNoteFormPayload) => {
    const item: ReleaseNoteItem = { ID: getNextId(releaseNotes), ...normalizeReleaseNotePayload(payload) }
    setReleaseNotes(previous => [...previous, item])
    return { success: true }
}

export const createUpdateReleaseNote = (
    setReleaseNotes: Dispatch<SetStateAction<ReleaseNoteItem[]>>
) => async (payload: ReleaseNoteFormPayload, original: ReleaseNoteItem) => {
    const normalizedPayload = normalizeReleaseNotePayload({ ...payload, Modified: new Date().toISOString() })
    setReleaseNotes(previous => previous.map(item => item.ID === original.ID
        ? { ...item, ...normalizedPayload }
        : item))
    return { success: true }
}

export const createDeleteReleaseNote = (
    setReleaseNotes: Dispatch<SetStateAction<ReleaseNoteItem[]>>
) => async (item: ReleaseNoteItem) => {
    setReleaseNotes(previous => previous.filter(current => current.ID !== item.ID))
    return { success: true }
}